import { Dispatch } from 'react';

import { UnionToIntersection } from '@rbi-ctg/frontend';

export interface Event extends Record<string, any> {
  type: string;
}

export interface Accumulator<Context, Services> {
  value: string;
  context: Context;
  services: Services;
}

export type Action<Context, Services> = (
  accumulator: Accumulator<Context, Services>,
  event: Event
) => Accumulator<Context, Services>;

export type Send = Dispatch<Event>;

export type Effect<Context, Services> = (
  accumulator: Accumulator<Context, Services>,
  send: Send
) => void;

export interface StatesShape {
  [states: string]: {
    on?: {
      [action: string]: {};
    };
  };
}

interface States<Context, Services> extends StatesShape {
  [states: string]: {
    entry?: Effect<Context, Services>;
    exit?: Effect<Context, Services>;
    on?: {
      [action: string]: {
        action?: Action<Context, Services>;
        effect?: Effect<Context, Services>;
      };
    };
  };
}

export interface Machine<Context = undefined, Services = undefined> {
  initial: string;
  states: States<Context, Services>;
}

export interface EventGeneric<T extends StatesShape> extends Record<string, any> {
  type: keyof UnionToIntersection<Extract<T[keyof T], { on: {} }>['on']>;
}

export type ActionGeneric<Context, Services, T extends StatesShape> = (
  accumulator: AccumulatorGeneric<Context, Services, T>,
  event: EventGeneric<T>
) => AccumulatorGeneric<Context, Services, T>;

export interface AccumulatorGeneric<Context, Services, T extends StatesShape> {
  value: keyof T;
  context: Context;
  services: Services;
}

export type SendGeneric<T extends StatesShape> = Dispatch<EventGeneric<T>>;

export type EffectGeneric<Context, Services, T extends StatesShape> = (
  accumulator: AccumulatorGeneric<Context, Services, T>,
  send: SendGeneric<T>
) => void | Promise<void>;

export type AccumulatorEffectTuple<Context, Services, T extends StatesShape> = [
  AccumulatorGeneric<Context, Services, T>,
  EffectGeneric<Context, Services, T>?
];

export enum MajorPrizes {
  WIN_SKULLCANDY_CODE = 'win-skullcandy-code',
  WIN_XBOX_CODE = 'win-xbox-code',
  WIN_CINEPLEX_CODE = 'win-cineplex-code',
  WIN_CRAVE_SUB_STANDARD = 'win-crave-sub-standard',
  WIN_SPOTIFY_SUB = 'win-spotify-sub',
  WIN_ATHLETIC_SUB_STANDARD = 'win-athletic-sub-standard',
  WIN_SPORTSNET_CODE = 'win-sportsnet-code',
  WIN_UBEREATS_CODE = 'win-ubereats-code',
  WIN_TAOS = 'win-taos',
  WIN_GROCERIES = 'win-groceries',
  WIN_FAIRMONT_ROYAL_YORK = 'win-fairmont-royal-york',
  WIN_FAIRMONT_QUEEN_ELIZABETH = 'win-fairmont-queen-elizabeth',
  WIN_FAIRMONT_CHATEAU_FRONTENAC = 'win-fairmont-chateau-frontenac',
  WIN_FAIRMONT_CHATEAU_MONTEBELLO = 'win-fairmont-chateau-montebello',
  WIN_FAIRMONT_BANFF_SPRINGS = 'win-fairmont-banff-springs',
  WIN_FAIRMONT_JASPER_PARK = 'win-fairmont-jasper-park',
  WIN_FAIRMONT_CHATEAU_LAKE_LOUISE = 'win-fairmont-chateau-lake-louise',
  WIN_FAIRMONT_CHATEAU_WHISTLER = 'win-fairmont-chateau-whistler',
  WIN_FAIRMONT_VANCOUVER = 'win-fairmont-vancouver',
  WIN_FAIRMONT_THE_EXPRESS = 'win-fairmont-the-express',
  WIN_FAIRMONT_GETAWAY_WEEKEND = 'win-fairmont-getaway-weekend',
  WIN_GALAXY_S21 = 'win-galaxy-s21',
  WIN_GALAXY_A51 = 'win-galaxy-a51',
  WIN_65_TV = 'win-65-tv',
  WIN_SKULLCANDY_EARBUDS = 'win-skullcandy-earbuds',
  WIN_XBOX_SERIES_S = 'win-xbox-series-s',
  WIN_1000_AMEX = 'win-1000-amex',
  WIN_PARKS_CANADA = 'win-parks-canada',
  WIN_100_PARKLAND_FUELS = 'win-100-parkland-fuels',
  WIN_CINEPLEX_GRAND = 'win-cineplex-grand',
  WIN_100_HOME_HARDWARE = 'win-100-home-hardware',
  WIN_CRAVE_SUB_GRAND = 'win-crave-sub-grand',
  WIN_ATHLETIC_SUB_GRAND = 'win-athletic-sub-grand',
}

export enum GiftCards {
  WIN_25_TIMSCARD = 'win-25-timcard',
}

export enum MinorPrizes {
  WIN_DREAM_DONUT = 'free-dream-donut',
}

export enum OfferPrizes {
  WIN_COFFEE = 'win-coffee',
  WIN_DONUT = 'win-donut',
  WIN_REUSABLE_CUP = 'win-reusable-cup',
}
